<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import IconChevronDown from '@lahaus-roomie/roomie/src/assets/icons/chevron-down.svg'
import IconChevronLeft from '@lahaus-roomie/roomie/src/assets/icons/chevron-left.svg'
import RoomieInput from '@lahaus-roomie/roomie/src/components/RoomieInput/index.vue'
import IconSend from '@lahaus-roomie/roomie/src/assets/icons/send.svg'
import { storeToRefs } from 'pinia'
import type { Props } from './types'
import type { OnboardingRecommendationFragmentActionable } from '@/components/OnboardingContent/ChatOverlay/types'
import type { OutstandingRecommendation } from '@/composables/useSamGpt/types'
import AppAiChatBubbles from '@/components/App/Ai/Chat/Bubbles/index.vue'
import ContactForm from '@/components/ContactForm/index.vue'
import { convertSearchFiltersToQueryObject } from '@/components/SearchFilters/utils'
import { buildQueryString } from '@/utils/urls'
import { TYPE_ATTENTION, SCREEN, SCREEN_CTA } from '@/utils/segment'

import { useAppChatStore } from '@/stores/appChatStore'

const { t } = useI18n()
const breakpoints = useBreakpoints(breakpointsTailwind)
const isDesktop = breakpoints.greater('md')

const appChatStore = useAppChatStore()

const { showForm } = storeToRefs(appChatStore)

const props = withDefaults(defineProps<Props>(), {
  screen: SCREEN.PDP_INVESTORS,
  screenCta: SCREEN_CTA.INVESTMENT_WEB_CHAT,
  openAfterInit: false
})

const botAgentImage = 'https://lahaus.imgix.net/static/lh-landing/img-sam-profile.png?auto=compress,enhance,format'
const botAgentName = 'Sam'
const isOpen = ref(false)
const currentMessage = ref('')
const lastScrollPosition = ref(0)
const refBubble = ref(null)

const toggleChat = () => {
  isOpen.value = !isOpen.value

  if (isOpen.value) {
    document.body.classList.add('chat-ai-open')
  } else {
    document.body.classList.remove('chat-ai-open')
  }

  if (!isDesktop.value) handleToggleShowChatWindowMobile()
}

const sendMessage = () => {
  if (!currentMessage.value) return

  refBubble.value?.handleSendMessage(currentMessage.value)

  currentMessage.value = ''
}

const handleToggleShowChatWindowMobile = () => {
  if (isOpen.value) {
    setTimeout(() => {
      handleBackgroundOverflow()
      window.scrollTo({ top: lastScrollPosition.value, behavior: 'instant' })
    })
  } else {
    lastScrollPosition.value = window.scrollY
    handleBackgroundOverflow()
  }
}

const handleBackgroundOverflow = () => {
  document.body.classList.toggle('no-background-scroll-with-height')
  document.documentElement.classList.toggle('no-background-scroll-with-height')
}

const handleOutstandingRecommendationClick = async (outstandingRecommendation: OutstandingRecommendation['card_params']) => {
  await navigateTo(outstandingRecommendation.path)
}

const handlePassiveRecommendationClick = (actionableClicked: OnboardingRecommendationFragmentActionable) => {
  let legacyStepToRedirect = 3

  if (actionableClicked.filters.opportunityType === 'living') {
    legacyStepToRedirect = 4
  }

  const parsedFilters = convertSearchFiltersToQueryObject(actionableClicked.filters)
  window.location.href = '/onboarding?' + buildQueryString({
    ...parsedFilters,
    step: legacyStepToRedirect
  })
}

onMounted(() => {
  if (props.openAfterInit) {
    setTimeout(() => {
      toggleChat()
    }, 500)
  }
})
</script>

<script lang="ts">
export default {
  name: 'AppAiChatWindow'
}
</script>

<template>
  <section
    class="app-ai-chat__window-holder transition-all py-24"
    :class="{
      'pt-0 open': isOpen
    }">
    <button
      id="app-ai-chat-expand-button-icon"
      :data-lh-id="`${screen}-app-ai-chat-expand-button`"
      type="button"
      class="absolute flex items-center top-3 right-8 md:right-8"
      @click="toggleChat">
      <IconChevronDown
        :class="{
          'transform rotate-180': !isOpen
        }"
        class="md:hidden w-32 h-32 transition-all fill-white pointer-events-none" />

      <IconChevronLeft
        :class="{
          'transform rotate-180': !isOpen
        }"
        class="hidden md:block w-32 h-32 transition-all fill-white pointer-events-none" />
    </button>

    <div
      v-if="!isOpen"
      class="w-full h-full cursor-pointer"
      :data-lh-id="`${screen}-app-ai-chat-expand-button`"
      @click="toggleChat">
      <img
        class="hidden md:block absolute bottom-0 w-full max-w-72 pointer-events-none"
        src="https://lahaus.imgix.net/static/lh-landing/SamNoBackground.png"
        alt="Sam">
    </div>

    <div
      v-if="!isOpen"
      class="md:hidden app-ai-chat__window w-full flex px-26 cursor-pointer"
      :data-lh-id="`${screen}-app-ai-chat-expand-button`"
      @click="toggleChat">
      <button
        id="app-ai-chat-expand-button-face"
        type="button"
        class="circular-button relative pointer-events-none">
        <img
          :src="botAgentImage"
          :alt="botAgentName"
          class="w-full h-full object-cover pointer-events-none">
      </button>

      <h2 class="flex-1 text-white font-semibold text-14 justify-center flex items-center pointer-events-none">
        {{ t('title') }}
      </h2>
    </div>

    <ContactForm
      v-else-if="showForm"
      id="web-chat-contact-form"
      class="px-24 h-full flex flex-col justify-center"
      dark-mode
      :title="appChatStore.contactFormConfig.contactFormTitleCopy"
      :cta-copie="appChatStore.contactFormConfig.contactFormCtaCopy"
      :screen
      :screen-cta
      :show-email-input="false"
      :show-name-input="false"
      :save-cookie="false"
      :skip-confirmation="true"
      :attention-type="TYPE_ATTENTION.PRODUCT_BOT" />

    <div
      v-else
      class="h-full flex flex-col">
      <div
        id="app-ai-chat-header-section"
        class="mx-24 md:px-0 flex items-center pt-16 pb-20 border-b border-carbon-300">
        <img
          data-lh-id="app-ai-chat-header-section"
          src="https://lahaus.imgix.net/static/lh-landing/img-sam-profile.png?auto=compress,enhance,format"
          class="w-48 h-48 object-cover mr-12 cursor-pointer"
          alt="Sam"
          @click="toggleChat">

        <span
          data-lh-id="app-ai-chat-header-section"
          class="text-20 text-white font-medium cursor-pointer"
          @click="toggleChat">
          Soy Sam AI
        </span>
      </div>

      <AppAiChatBubbles
        ref="refBubble"
        init-offline
        :offline-agent
        :screen
        :screen-cta
        class="overflow-auto text-white flex-1 px-26"
        @outstanding-recommendation-click="handleOutstandingRecommendationClick"
        @passive-recommendation-click="handlePassiveRecommendationClick" />

      <form
        class="app-ai-chat__send-container flex items-center gap-8 border border-carbon-600 rounded-[200px] mx-16"
        @submit.prevent="sendMessage">
        <RoomieInput
          id="app-ai-chat-input-send-message"
          v-model="currentMessage"
          autocomplete="off"
          class="border-0 text-white"
          placeholder="Escribe lo que necesites aquí"
          data-lh-id="app-ai-chat-input-send-message"
          :is-disabled="false"
          :value="currentMessage" />

        <button
          id="app-ai-chat-send-button"
          data-lh-id="app-ai-chat-send-button"
          :aria-label="t('sendMessageCta')"
          type="submit"
          class="w-52 h-48 px-12 py-8 mr-4 bg-primary-600 rounded-full">
          <IconSend
            class="fill-white pointer-events-none" />
        </button>
      </form>
    </div>
  </section>
</template>

<style lang="scss">
@import "@lahaus-roomie/roomie/src/assets/styles/base/variables";

.app-ai-chat {
  &__window-holder {
    @apply z-20 flex flex-col;
    @apply w-full backdrop-blur;
    @apply fixed bottom-0 left-0 md:w-72;

    background: radial-gradient(46.94% 27.21% at 3.06% 4.43%, rgba(31 121 101 / 92%) 0%, rgba(36 46 44 / 92%) 100%);
    height: 86px;

    @apply rounded-t-16 md:rounded-tl-0;

    &.open {
      height: calc(100% - 80px);
    }

    .circular-button {
      @apply w-44 h-44 mt-6 rounded-full shadow-[0_0_10px_#0000004f] flex items-center justify-center
    }

    .chat-bubbles {
      &::-webkit-scrollbar {
        @apply w-6;
      }

      &::-webkit-scrollbar-track {
        @apply bg-none;
      }

      &::-webkit-scrollbar-thumb {
        @apply rounded-8;
        @apply bg-carbon-light-300 hover:bg-carbon-light-600;
      }
    }

    @media(min-width: theme('screens.md')) {
      height: calc(100svh - 80px);

      &.open {
        width: 330px;
      }
    }

    @media(width >= 1200px) {
      &.open {
        width: 380px;
      }
    }
  }

  &__send-container {
    div {
      @apply w-full h-56;
    }

    &:focus-within {
        border: 0.1rem solid $brand-600;
        outline-width: 0.1rem;
        outline-color: $brand-600;
    }

    .roomie-input__container {
      border: none;
    }

    .roomie-input__container:focus-within {
      border: none;
      outline: none;
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
